import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, ViewChild, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../shared/icon/icon.component';
import { IssueAnnotation, IssueSnapshot } from '../issues.types';

/** Frozen page-snapshot renderer + annotation surface.
 *
 *  - When {@link editable} is false, the SVG overlays just render the
 *    stored boxes / arrows on top of the PNG (scaled to fit the
 *    available width).
 *  - When {@link editable} is true and {@link tool} is set, dragging on
 *    the surface builds a new annotation and emits it via
 *    {@code (annotationAdded)}.
 *
 *  Coordinates are unit-fraction (0..1) of the capture rect, so the
 *  same annotation renders correctly at any scale.
 */
@Component({
  selector: 'app-iss-snapshot',
  standalone: true,
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: '../issues.scss',
  template: `
    @if (!shot() || !shot()!.dataUrl) {
      <div class="iss-shot-empty" #wrap [style.height.px]="boxH()">
        <app-icon name="Camera" [size]="22"></app-icon>
        <div class="text-sm semibold mt-8">No page preview</div>
        <div class="text-tiny muted">
          @if (placeholderModule()) { Captured on {{ placeholderModule() }} }
          @else { This issue was filed without a snapshot. }
        </div>
      </div>
    } @else {
      <div class="iss-shot" #wrap [style.height.px]="boxH()">
        @if (scale() > 0) {
          <div class="iss-shot-stage" [style.width.px]="stageW()" [style.height.px]="stageH()">
            <img [src]="shot()!.dataUrl" class="iss-shot-img"
                 [style.width.px]="stageW()" [style.height.px]="stageH()" alt="Page snapshot" />
            <svg class="iss-anno" [class.drawing]="editable() && !!tool()"
                 [attr.width]="stageW()" [attr.height]="stageH()"
                 [attr.viewBox]="'0 0 ' + stageW() + ' ' + stageH()"
                 (mousedown)="down($event)" (mousemove)="move($event)" (mouseup)="up()" (mouseleave)="up()">
              @for (a of allAnnos(); track $index) {
                @if (a.type === 'box') {
                  <rect [attr.x]="a.x * stageW()" [attr.y]="a.y * stageH()"
                        [attr.width]="a.w * stageW()" [attr.height]="a.h * stageH()"
                        class="iss-anno-box" rx="3" />
                } @else {
                  <g>
                    <line [attr.x1]="a.x1 * stageW()" [attr.y1]="a.y1 * stageH()"
                          [attr.x2]="a.x2 * stageW()" [attr.y2]="a.y2 * stageH()"
                          class="iss-anno-arrow-line" />
                    <path [attr.d]="arrowHead(a)" class="iss-anno-arrow-head" />
                  </g>
                }
              }
            </svg>
          </div>
        }
      </div>
    }
  `,
})
export class IssSnapshotComponent implements AfterViewInit, OnDestroy {
  readonly shot = input<IssueSnapshot | null>(null);
  readonly annotations = input<IssueAnnotation[]>([]);
  readonly editable = input<boolean>(false);
  readonly tool = input<'box' | 'arrow' | null>(null);
  readonly boxH = input<number>(320);
  readonly placeholderModule = input<string>('');
  readonly annotationAdded = output<IssueAnnotation>();

  @ViewChild('wrap', { static: false }) wrapRef?: ElementRef<HTMLElement>;

  private readonly wrapW = signal<number>(0);
  private readonly draft = signal<IssueAnnotation | null>(null);
  private dragStart: { x: number; y: number } | null = null;
  private resizeObs?: ResizeObserver;

  /** {@code scale = min(boxW/capW, boxH/capH)} so the whole capture
   *  fits inside the wrapper regardless of aspect ratio. */
  readonly scale = computed<number>(() => {
    const s = this.shot();
    if (!s || !this.wrapW()) return 0;
    return Math.min(this.wrapW() / (s.w || 1440), this.boxH() / (s.h || 900));
  });
  readonly stageW = computed(() => (this.shot()?.w ?? 1440) * this.scale());
  readonly stageH = computed(() => (this.shot()?.h ?? 900)  * this.scale());

  readonly allAnnos = computed<IssueAnnotation[]>(() => {
    const d = this.draft();
    return d ? [...this.annotations(), d] : this.annotations();
  });

  ngAfterViewInit(): void {
    const el = this.wrapRef?.nativeElement;
    if (!el) return;
    this.wrapW.set(el.clientWidth);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.wrapW.set(el.clientWidth));
      this.resizeObs.observe(el);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  }
  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }
  private readonly onResize = () => this.wrapW.set(this.wrapRef?.nativeElement.clientWidth ?? 0);
  @HostListener('window:orientationchange') onOrient() { this.onResize(); }

  /** Unit-fraction position of the pointer inside the SVG. */
  private pt(e: MouseEvent): { x: number; y: number } {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (e.clientY - r.top)  / r.height)),
    };
  }

  down(e: MouseEvent): void {
    if (!this.editable() || !this.tool()) return;
    const p = this.pt(e);
    this.dragStart = p;
    this.draft.set(this.tool() === 'box'
      ? { type: 'box',   x: p.x, y: p.y, w: 0, h: 0 }
      : { type: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  }
  move(e: MouseEvent): void {
    if (!this.dragStart) return;
    const p = this.pt(e); const s = this.dragStart;
    this.draft.set(this.tool() === 'box'
      ? { type: 'box',   x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) }
      : { type: 'arrow', x1: s.x, y1: s.y, x2: p.x, y2: p.y });
  }
  up(): void {
    const d = this.draft();
    if (d) {
      // Only commit non-degenerate shapes so a stray click doesn't
      // spam annotations (matches the JSX reference threshold).
      const big = d.type === 'box'
        ? (d.w > 0.02 && d.h > 0.02)
        : (Math.abs(d.x2 - d.x1) + Math.abs(d.y2 - d.y1) > 0.03);
      if (big) this.annotationAdded.emit(d);
    }
    this.dragStart = null;
    this.draft.set(null);
  }

  /** Arrow-head SVG path in absolute (stage-space) coordinates. */
  arrowHead(a: Extract<IssueAnnotation, { type: 'arrow' }>): string {
    const W = this.stageW(), H = this.stageH();
    const x1 = a.x1 * W, y1 = a.y1 * H, x2 = a.x2 * W, y2 = a.y2 * H;
    const ang = Math.atan2(y2 - y1, x2 - x1);
    const head = 11;
    const hx1 = x2 - head * Math.cos(ang - Math.PI / 7);
    const hy1 = y2 - head * Math.sin(ang - Math.PI / 7);
    const hx2 = x2 - head * Math.cos(ang + Math.PI / 7);
    const hy2 = y2 - head * Math.sin(ang + Math.PI / 7);
    return `M${x2} ${y2} L${hx1} ${hy1} M${x2} ${y2} L${hx2} ${hy2}`;
  }
}
